import styled, { css } from 'styled-components';

import { Box } from '@/components/Box';
import { LegacyButton as BaseButton } from '@/components/LegacyButton';
import LinkWithLocale from '@/components/LinkWithLocale/LinkWithLocale';
import { Typography } from '@/components/Typography';
import type { Maybe } from '@/lib/types';
import { theme } from '@/theme';

import { ReactComponent as ChevronSvg } from '@/assets/icons/chevron.svg';

export const ButtonWrapper = styled(Box)`
  margin: 10px auto 0 auto;
  width: 100%;
  ${theme.media.md.up} {
    width: 300px;
  }
`;

export const DetailContainer = styled(Box)`
  ${({ theme }) => css`
    max-width: 1100px;
    margin-right: auto;
    margin-left: auto;
    margin-bottom: ${theme.spacing(2.5)};
    ${theme.media.md.up} {
      max-width: 1100px;
      margin-right: auto;
      margin-left: auto;
      margin-bottom: ${theme.spacing(5)};
    }
  `};
`;
export const DetailContent = styled(Box)`
  ${({ theme }) => css`
    background-color: ${theme.colors.white};
    position: relative;
    padding-right: ${theme.spacing(2.5)};
    padding-bottom: ${theme.spacing(0)};
    padding-left: ${theme.spacing(2.5)};
    padding-top: ${theme.spacing(5)};
    ${theme.media.md.up} {
      background-color: ${theme.colors.white};
      position: relative;
      padding: ${theme.spacing(5)} ${theme.spacing(5)} ${theme.spacing(0)}
        ${theme.spacing(5)};
    }
  `};
`;
export const InfoContainer = styled(Box)`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    ${theme.media.md.up} {
      display: flex;
      flex-direction: row;
    }
  `};
`;
export const CtaContainer = styled(Box)`
  display: flex;
  justify-content: center;
`;
export const AddressContainer = styled(Box)`
  ${({ theme }) => css`
    position: relative;
    margin-bottom: 20px;
    padding-right: 50px;
    ${theme.media.md.up} {
      position: relative;
      margin-bottom: 20px;
      padding-right: 50px;
      max-width: 33.333333333%;
      min-width: 33.333333333%;
    }
  `};
`;
export const ContactContainer = styled(Box)`
  ${({ theme }) => css`
    margin-bottom: 20px;
    padding-right: 50px;
    ${theme.media.md.up} {
      margin-bottom: 20px;
      padding-right: 50px;
      max-width: 33.333333333%;
      min-width: 33.333333333%;
    }
  `};
`;
export const OpeningHoursContainer = styled(Box)`
  ${({ theme }) => css`
    margin-bottom: 20px;
    padding-right: 50px;
    ${theme.media.md.up} {
      margin-bottom: 20px;
      padding-right: 50px;
      max-width: 33.333333333%;
      min-width: 33.333333333%;
    }
  `};
`;
export const InfoTitleWrapper = styled(Box)`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`;
export const InfoTitle = styled(Typography)`
  font-weight: 500;
  text-transform: uppercase;
  line-height: 1;
  ${({ theme }) => css`
    color: ${theme.colors.secondary};
    font-size: ${theme.fontSizes.h5};
    font-family: ${theme.fontFamilies.italianPlate};
  `};
`;
export const ItemsWrapper = styled(Box)`
  padding-top: 10px;
`;
export const ContactItem = styled(Box)`
  display: flex;
  justify-content: flex-start;
`;
export const ContactItemName = styled(Box)`
  line-height: 1.45;
  letter-spacing: 0;
  font-size: 14px;
  margin-right: 13px;
  min-width: 42px;
  max-width: 70px;
`;
export const OpenTextWrapper = styled(Box)`
  margin-top: 16px;
  line-height: 1.45;
  letter-spacing: 0;
  font-size: 14px;
`;
export const StyledSpan = styled.span<{ color?: Maybe<string> }>`
  display: inline-block;
  font-weight: 700;
  ${({ color }) => css`
    background-color: ${color};
  `}
  margin-right: 5px;
  border-radius: 50%;
  height: 10px;
  max-width: 10px;
  min-width: 10px;
`;
export const WeekdayName = styled(Box)`
  line-height: 1.45;
  letter-spacing: 0;
  font-size: ${theme.fontSizes.p2};
  margin-right: 16px;
  width: 76px;
`;
export const ContactItemContent = styled(Box)`
  line-height: 1.45;
  letter-spacing: 0;
  font-size: ${theme.fontSizes.p2};
`;
export const WeekDayHours = styled(Box)`
  line-height: 1.45;
  letter-spacing: 0;
  font-size: ${theme.fontSizes.p2};
`;
export const AddressLineContent = styled(Box)`
  max-width: 220px;
  line-height: 1.45;
  letter-spacing: 0;
  font-size: ${theme.fontSizes.p2};
  text-transform: capitalize;
`;
export const AddressCountryContent = styled(Box)`
  line-height: 1.45;
  letter-spacing: 0;
  font-size: ${theme.fontSizes.p2};
  text-transform: uppercase;
`;
export const StyledLink = styled(LinkWithLocale)`
  transition: all 0.2s ease-in-out;
  ${({ theme }) => css`
    color: ${theme.colors.primaryBlue};
  `};
  font-weight: 400;
  text-decoration: underline;
  &:hover {
    ${({ theme }) => css`
      color: ${theme.colors.primary};
    `};
  }
`;
export const GetDirectionCta = styled(Box)`
  ${({ theme }) => css`
    position: absolute;
    bottom: 0;
    right: 0;
    ${theme.media.md.up} {
      margin-top: 10px;
      left: 0;
      right: auto;
      position: relative;
    }
  `};
`;

export const DistanceWrapper = styled(Box)`
  display: flex;
  justify-content: flex-end;
  ${({ theme }) => css`
    position: absolute;
    top: 32px;
    right: 10px;
    ${theme.media.md.up} {
      position: absolute;
      top: 32px;
      right: 35px;
    }
  `};
`;
export const Distance = styled(Box)`
  ${({ theme }) => css`
    font-size: ${theme.fontSizes.p3};
    color: ${theme.colors.default};
  `}
  line-height: 17px;
  margin-left: 4px;
  white-space: nowrap;
`;
export const Button = styled(BaseButton)<{
  color?: string;
  lower?: boolean;
  underline?: boolean;
}>`
  text-decoration: none;
  ${({ lower }) =>
    lower &&
    css`
      text-transform: initial;
      font-weight: 500;
    `}
  ${({ underline }) =>
    underline &&
    css`
      text-decoration: underline;
      text-underline-offset: 1px;
      height: 22px;
      font-weight: 700;
      text-decoration-thickness: 1px;
    `}
`;
export const Chevron = styled(ChevronSvg)`
  fill: ${({ theme }) => theme.colors.primary};
`;
